import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Settings, ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { getCourierSettings, updateCourierSettings, listCourierApplications, setCourierApplicationStatus } from "../lib/api";

const GREEN = "#075A43";
const field = { background: "#f5f7f6", border: "1px solid #e5e7eb" };
const inp = "w-full rounded-xl px-4 h-11 text-sm outline-none";

export default function CourierSettings() {
  const nav = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin" || user?.role === "manager";
  const [s, setS] = useState(null);
  const [apps, setApps] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (p) => setS((o) => ({ ...o, ...p }));

  const load = () => {
    getCourierSettings().then(setS).catch(() => {});
    listCourierApplications().then(setApps).catch(() => {});
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) return <div className="max-w-xl mx-auto px-4 py-16 text-center" style={{ color: "#64748b" }} data-testid="cs-denied">Réservé au Fondateur.</div>;
  if (!s) return <div className="max-w-xl mx-auto px-4 py-16 text-center" style={{ color: "#64748b" }}>…</div>;

  const save = async () => {
    setBusy(true);
    try {
      await updateCourierSettings({
        base_fee_chf: parseFloat(s.base_fee_chf) || 0, per_km_chf: parseFloat(s.per_km_chf) || 0,
        express_surcharge_chf: parseFloat(s.express_surcharge_chf) || 0,
        c2c_commission_pct: parseFloat(s.c2c_commission_pct) || 0,
        vehicles: s.vehicles, weight_tiers: s.weight_tiers, size_tiers: s.size_tiers,
      });
      toast.success("Réglages enregistrés"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };
  const act = async (id, status) => {
    setBusy(true);
    try { await setCourierApplicationStatus(id, status); toast.success(status === "approved" ? "Livreur validé" : "Dossier refusé"); load(); }
    catch { toast.error("Échec"); } finally { setBusy(false); }
  };
  const setVeh = (i, mult) => setS((o) => ({ ...o, vehicles: o.vehicles.map((v, j) => j === i ? { ...v, multiplier: parseFloat(mult) || 0 } : v) }));
  const setWt = (i, sur) => setS((o) => ({ ...o, weight_tiers: o.weight_tiers.map((t, j) => j === i ? { ...t, surcharge_chf: parseFloat(sur) || 0 } : t) }));

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="courier-settings-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm font-bold mb-4" style={{ color: GREEN }}><ArrowLeft size={18} /> Retour</button>
      <div className="flex items-center gap-2 mb-1"><Settings size={24} color={GREEN} /><h1 className="text-2xl font-extrabold" style={{ color: GREEN }}>Livraison colis — Réglages</h1></div>
      <p className="text-sm mb-4" style={{ color: "#64748b" }}>Tarifs au km / poids / taille / véhicule (livreurs La BASE) et commission entre particuliers.</p>

      <div className="rounded-2xl p-4 space-y-3 mb-6" style={{ border: "1px solid #f0f0f0" }} data-testid="cs-tarifs">
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs font-bold" style={{ color: "#64748b" }}>Forfait de base (CHF)<input data-testid="cs-base" value={s.base_fee_chf} onChange={(e) => set({ base_fee_chf: e.target.value })} className={inp} style={field} /></label>
          <label className="text-xs font-bold" style={{ color: "#64748b" }}>Prix / km (CHF)<input data-testid="cs-perkm" value={s.per_km_chf} onChange={(e) => set({ per_km_chf: e.target.value })} className={inp} style={field} /></label>
          <label className="text-xs font-bold" style={{ color: "#64748b" }}>Supplément express (CHF)<input data-testid="cs-express" value={s.express_surcharge_chf} onChange={(e) => set({ express_surcharge_chf: e.target.value })} className={inp} style={field} /></label>
          <label className="text-xs font-bold" style={{ color: "#64748b" }}>Commission C2C (%)<input data-testid="cs-commission" value={s.c2c_commission_pct} onChange={(e) => set({ c2c_commission_pct: e.target.value })} className={inp} style={field} /></label>
        </div>
        <p className="text-xs font-bold" style={{ color: "#64748b" }}>Multiplicateur par véhicule</p>
        {(s.vehicles || []).map((v, i) => (
          <div key={v.key} className="flex items-center gap-2"><span className="text-sm flex-1">{v.label}</span>
            <input data-testid={`cs-veh-${v.key}`} value={v.multiplier} onChange={(e) => setVeh(i, e.target.value)} className="w-24 rounded-lg px-2 h-10 text-sm outline-none" style={field} /></div>
        ))}
        <p className="text-xs font-bold" style={{ color: "#64748b" }}>Supplément par palier de poids (CHF)</p>
        {(s.weight_tiers || []).map((t, i) => (
          <div key={t.label} className="flex items-center gap-2"><span className="text-sm flex-1">{t.label}</span>
            <input data-testid={`cs-wt-${i}`} value={t.surcharge_chf} onChange={(e) => setWt(i, e.target.value)} className="w-24 rounded-lg px-2 h-10 text-sm outline-none" style={field} /></div>
        ))}
        <button data-testid="cs-save" onClick={save} disabled={busy} className="w-full rounded-xl font-bold text-white h-11" style={{ background: GREEN, opacity: busy ? 0.6 : 1 }}>Enregistrer</button>
      </div>

      <div className="flex items-center gap-2 mb-2"><ShieldCheck size={18} color={GREEN} /><h2 className="font-extrabold" style={{ color: GREEN }}>Validation des livreurs (KYC)</h2></div>
      <div className="space-y-2" data-testid="cs-apps">
        {apps.length === 0 && <p className="text-sm" style={{ color: "#64748b" }}>Aucune demande.</p>}
        {apps.map((a) => (
          <div key={a.id} data-testid={`cs-app-${a.id}`} className="rounded-xl p-3 flex items-center justify-between" style={{ border: "1px solid #f0f0f0" }}>
            <div className="min-w-0">
              <p className="font-bold text-sm">{a.full_name} <span className="text-xs font-normal" style={{ color: "#64748b" }}>· {a.vehicle} · {a.status}</span></p>
              <p className="text-xs truncate" style={{ color: "#64748b" }}>{a.phone} · {a.address}</p>
              <div className="flex gap-2 mt-1">{a.id_doc_url && <a href={a.id_doc_url} target="_blank" rel="noreferrer" className="text-xs font-bold" style={{ color: GREEN }}>Pièce ID</a>}{a.selfie_url && <a href={a.selfie_url} target="_blank" rel="noreferrer" className="text-xs font-bold" style={{ color: GREEN }}>Selfie</a>}</div>
            </div>
            {a.status !== "approved" && <button data-testid={`cs-approve-${a.id}`} onClick={() => act(a.id, "approved")} disabled={busy} className="text-xs font-bold text-white px-3 h-8 rounded-lg shrink-0" style={{ background: GREEN }}>Valider</button>}
            {a.status !== "rejected" && <button data-testid={`cs-reject-${a.id}`} onClick={() => act(a.id, "rejected")} disabled={busy} className="text-xs font-bold px-3 h-8 rounded-lg shrink-0 ml-2" style={{ background: "#fef2f2", color: "#dc2626" }}>Refuser</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
